// Group chat websocket client (RN global WebSocket — no dependency).
//
// Lifecycle: connect once while authenticated + foregrounded; disconnect on
// background. Auto-reconnect with backoff; on (re)connect the caller should
// refetch the newest chat page — the keyset cursor returns exactly what was
// missed, which is why dropped broadcasts are a non-issue.

import { Platform } from 'react-native';
import { API_URL } from '@/lib/api';

export type ChatWsEvent =
  | { event: 'message.new'; group_id: string; message: any }
  | { event: 'message.edited'; group_id: string; message: any }
  | { event: 'message.deleted'; group_id: string; message_id: string }
  | { event: 'poll.voted'; group_id: string; poll: any }
  | { event: 'poll.closed'; group_id: string; poll: any };

type Handler = (e: ChatWsEvent) => void;

const handlers = new Set<Handler>();
let ws: WebSocket | null = null;
let activeToken: string | null = null;
let shouldRun = false;
let reconnectAttempt = 0;
let reconnectTimer: ReturnType<typeof setTimeout> | null = null;
let appStateSub: { remove(): void } | null = null;

function backoffMs() {
  return Math.min(1000 * 2 ** reconnectAttempt, 30000);
}

function scheduleReconnect() {
  if (!shouldRun || reconnectTimer || !activeToken) return;
  const token = activeToken;
  reconnectTimer = setTimeout(() => {
    reconnectTimer = null;
    connect(token);
  }, backoffMs());
}

function handleMessage(raw: string) {
  try {
    const event = JSON.parse(raw) as ChatWsEvent;
    handlers.forEach((h) => {
      try {
        h(event);
      } catch {
        // one bad handler never breaks the pipe
      }
    });
  } catch {
    // ignore malformed frames
  }
}

export function connect(token: string) {
  if (Platform.OS === 'web') return; // chat polling covers web
  activeToken = token;
  shouldRun = true;

  if (ws && (ws.readyState === WebSocket.OPEN || ws.readyState === WebSocket.CONNECTING)) return;

  // RN global WebSocket: http(s)→ws(s) scheme swap is manual
  const base = API_URL;
  const wsUrl = `${base.replace(/^http/, 'ws')}/ws?token=${encodeURIComponent(token)}`;

  try {
    ws = new WebSocket(wsUrl);
  } catch {
    scheduleReconnect();
    return;
  }

  ws.onopen = () => {
    reconnectAttempt = 0;
  };
  ws.onmessage = (e) => handleMessage(String(e.data));
  ws.onclose = () => {
    ws = null;
    if (shouldRun) scheduleReconnect();
  };
  ws.onerror = () => {
    try {
      ws?.close();
    } catch {}
  };
}

export function disconnect() {
  shouldRun = false;
  activeToken = null;
  if (reconnectTimer) {
    clearTimeout(reconnectTimer);
    reconnectTimer = null;
  }
  try {
    ws?.close();
  } catch {}
  ws = null;
}

export function isWsConnected(): boolean {
  return !!ws && ws.readyState === WebSocket.OPEN;
}

export function subscribeChatEvents(handler: Handler): () => void {
  handlers.add(handler);
  return () => handlers.delete(handler);
}
// ─── App lifecycle wiring ────────────────────────────────────────────
// The caller (root layout) provides a token getter because auth state lives
// in React; we re-read it on every foreground so reconnects always use the
// current session.

import { AppState } from 'react-native';

let tokenProvider: (() => string | null) | null = null;
let lifecycleStarted = false;

export function initWsLifecycle(getToken: () => string | null): void {
  if (lifecycleStarted) return;
  lifecycleStarted = true;
  tokenProvider = getToken;

  const sync = () => {
    const token = tokenProvider?.() ?? null;
    if (token) {
      if (activeToken !== token) {
        // session changed (login/relogin) — restart the socket
        disconnect();
        connect(token);
      } else if (!ws) {
        connect(token);
      }
    } else {
      disconnect();
    }
  };

  AppState.addEventListener('change', (state) => {
    if (state === 'active') sync();
    else if (state === 'background') disconnect();
  });

  sync();
}